import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";

function areIdListsEqual(left = [], right = []) {
  if (left === right) return true;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (String(left[index]) !== String(right[index])) {
      return false;
    }
  }

  return true;
}

function normalizeSelectionIds(ids = []) {
  return Array.from(
    new Set(
      ids
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0),
    ),
  );
}

const RepairProjectHierarchySelector = ({
  bridgeId,
  onSelectionChange,
  initialSelectedIds = [],
  showTopFilters = true,
  showSelectionSummary = true,
  clearSelectionOnBranchChange = false,
}) => {
  const [selectedBridge, setSelectedBridge] = useState(null);
  const [loadingHierarchy, setLoadingHierarchy] = useState(false);
  const [bulkSelecting, setBulkSelecting] = useState(false);
  const [loadingSelectionTable, setLoadingSelectionTable] = useState(false);
  const [selectedPath, setSelectedPath] = useState({});
  const [hierarchyData, setHierarchyData] = useState({});
  const [selectedElementIds, setSelectedElementIds] = useState([]);
  const [selectedElementsCache, setSelectedElementsCache] = useState({});
  const [selectionTableRows, setSelectionTableRows] = useState([]);
  const [descendantAreaIdsByParentId, setDescendantAreaIdsByParentId] =
    useState({});
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(true);
  const lastNotifiedSelectionRef = useRef(null);
  const appliedInitialSelectionRef = useRef(null);
  const hydratingSelectionSignatureRef = useRef(null);
  const pendingVisiblePathHydrationRef = useRef(null);

  const clearCurrentSelection = useCallback(() => {
    setSelectedElementIds([]);
    setSelectedElementsCache({});
    setSelectionTableRows([]);
    setDescendantAreaIdsByParentId({});
    lastNotifiedSelectionRef.current = null;
  }, []);

  const normalizedInitialSelectedIds = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(initialSelectedIds) ? initialSelectedIds : [])
            .map((id) => Number(id))
            .filter((id) => Number.isInteger(id) && id > 0),
        ),
      ),
    [initialSelectedIds],
  );

  const initialSelectionSignature = useMemo(
    () => normalizedInitialSelectedIds.map((id) => String(id)).join(","),
    [normalizedInitialSelectedIds],
  );

  useEffect(() => {
    const fetchBridgeDetails = async () => {
      if (!bridgeId) {
        setSelectedBridge(null);
        return;
      }

      try {
        const response = await api.get(`/bridges/${bridgeId}`);
        if (response.data?.success) {
          setSelectedBridge(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching bridge details:", error);
        setSelectedBridge(null);
      }
    };

    fetchBridgeDetails();
  }, [bridgeId]);

  useEffect(() => {
    lastNotifiedSelectionRef.current = null;
    appliedInitialSelectionRef.current = null;
    hydratingSelectionSignatureRef.current = null;
    pendingVisiblePathHydrationRef.current = null;
    setSelectedPath({});
    setHierarchyData({});
    setSelectedElementIds([]);
    setSelectedElementsCache({});
    setSelectionTableRows([]);
    setDescendantAreaIdsByParentId({});
    setIsSummaryCollapsed(true);
  }, [bridgeId]);

  useEffect(() => {
    if (!bridgeId) {
      return;
    }

    if (appliedInitialSelectionRef.current === initialSelectionSignature) {
      return;
    }

    appliedInitialSelectionRef.current = initialSelectionSignature;
    pendingVisiblePathHydrationRef.current = initialSelectionSignature;

    if (areIdListsEqual(selectedElementIds, normalizedInitialSelectedIds)) {
      lastNotifiedSelectionRef.current = initialSelectionSignature;
      hydratingSelectionSignatureRef.current = null;
      return;
    }

    hydratingSelectionSignatureRef.current = initialSelectionSignature;
    setSelectedElementIds(normalizedInitialSelectedIds);
  }, [
    bridgeId,
    initialSelectionSignature,
    normalizedInitialSelectedIds,
    selectedElementIds,
  ]);

  const getEnabledLevels = useCallback(() => {
    if (!selectedBridge || !selectedBridge.enabledLevels) return [1, 2, 3];
    const enabledLevels =
      typeof selectedBridge.enabledLevels === "string"
        ? JSON.parse(selectedBridge.enabledLevels)
        : selectedBridge.enabledLevels;
    return Array.isArray(enabledLevels) ? enabledLevels : [1, 2, 3];
  }, [selectedBridge]);

  const getAreaDataLevel = useCallback(() => {
    if (!selectedBridge || !selectedBridge.levelLabels) return 2;
    const levelLabels =
      typeof selectedBridge.levelLabels === "string"
        ? JSON.parse(selectedBridge.levelLabels)
        : selectedBridge.levelLabels;

    for (let i = 1; i <= 8; i += 1) {
      const levelKey = `level${i}`;
      if (levelLabels[levelKey] && levelLabels[levelKey].holdsAreaData) {
        return i;
      }
    }

    return 2;
  }, [selectedBridge]);

  const getLevelLabel = useCallback(
    (levelNum) => {
      if (!selectedBridge || !selectedBridge.levelLabels) {
        return `Level ${levelNum}`;
      }
      const levelLabels =
        typeof selectedBridge.levelLabels === "string"
          ? JSON.parse(selectedBridge.levelLabels)
          : selectedBridge.levelLabels;
      const levelKey = `level${levelNum}`;
      const label = levelLabels[levelKey]?.label || `Level ${levelNum}`;
      return label === "Direction" ? "Item" : label;
    },
    [selectedBridge],
  );

  const getRawLevelLabel = useCallback(
    (levelNum) => {
      if (!selectedBridge || !selectedBridge.levelLabels) {
        return `Level ${levelNum}`;
      }
      const levelLabels =
        typeof selectedBridge.levelLabels === "string"
          ? JSON.parse(selectedBridge.levelLabels)
          : selectedBridge.levelLabels;
      const levelKey = `level${levelNum}`;
      return levelLabels[levelKey]?.label || `Level ${levelNum}`;
    },
    [selectedBridge],
  );

  const levelsToShowAfterFilters = useMemo(() => {
    const areaLevel = getAreaDataLevel();
    const filtered = getEnabledLevels()
      .filter((level) => level <= areaLevel)
      .filter((level) => {
        const label = getLevelLabel(level);
        return (
          (label !== "Item" && label !== "Direction" && label !== "Position") ||
          level === areaLevel
        );
      });
    return filtered.length > 0 ? filtered : [areaLevel];
  }, [getAreaDataLevel, getEnabledLevels, getLevelLabel]);

  const penultimateLevel =
    levelsToShowAfterFilters.length >= 2
      ? levelsToShowAfterFilters[levelsToShowAfterFilters.length - 2]
      : null;
  const firstVisibleLevel = levelsToShowAfterFilters[0] || null;

  const selectionSummaryLevels = useMemo(
    () =>
      getEnabledLevels()
        .filter((levelNum) => levelNum <= getAreaDataLevel())
        .sort((a, b) => a - b),
    [getAreaDataLevel, getEnabledLevels],
  );

  useEffect(() => {
    const fetchHierarchy = async () => {
      if (!bridgeId || !selectedBridge) return;

      try {
        setLoadingHierarchy(true);

        const [directionsResponse, positionsResponse] = await Promise.all([
          api.get(`/new-simulation/directions/${bridgeId}`),
          api.get(`/new-simulation/positions/${bridgeId}`),
        ]);

        setHierarchyData((prev) => ({
          ...prev,
          directions: directionsResponse.data?.success
            ? directionsResponse.data.data
            : [],
          positions: positionsResponse.data?.success
            ? positionsResponse.data.data
            : [],
        }));
      } catch (error) {
        console.error("Error fetching hierarchy:", error);
      } finally {
        setLoadingHierarchy(false);
      }
    };

    fetchHierarchy();
  }, [bridgeId, selectedBridge]);

  useEffect(() => {
    const fetchChildren = async () => {
      if (!bridgeId || !selectedBridge) return;

      const enabledLevels = getEnabledLevels();
      const areaDataLevel = getAreaDataLevel();

      let highestLevel = 0;
      let parentId = null;

      for (let i = 1; i <= 8; i += 1) {
        if (selectedPath[`level${i}`]) {
          highestLevel = i;
          parentId = selectedPath[`level${i}`];
        }
      }

      const parentIds = parentId ? [parentId] : [];

      if (highestLevel > 0 && parentIds.length > 0) {
        const childLevel = highestLevel + 1;

        if (childLevel <= areaDataLevel && enabledLevels.includes(childLevel)) {
          try {
            setLoadingHierarchy(true);
            const allChildren = [];
            const seenChildIds = new Set();

            for (const currentParentId of parentIds) {
              const response = await api.get(
                `/new-simulation/hierarchy/${bridgeId}?level=${childLevel}&parentId=${currentParentId}`,
                { skipGlobalLoading: true },
              );

              if (response.data?.success && Array.isArray(response.data.data)) {
                response.data.data.forEach((child) => {
                  if (!seenChildIds.has(child.id)) {
                    seenChildIds.add(child.id);
                    allChildren.push(child);
                  }
                });
              }
            }

            setHierarchyData((prev) => ({
              ...prev,
              [`level${childLevel}`]: allChildren,
            }));
          } catch (error) {
            console.error(`Error fetching level ${childLevel}:`, error);
          } finally {
            setLoadingHierarchy(false);
          }
        }
      }

      if (enabledLevels.includes(areaDataLevel)) {
        let areaDataParentId = null;
        for (let i = areaDataLevel - 1; i >= 1; i -= 1) {
          if (selectedPath[`level${i}`]) {
            areaDataParentId = selectedPath[`level${i}`];
            break;
          }
        }

        if (areaDataParentId) {
          try {
            const response = await api.get(
              `/new-simulation/hierarchy/${bridgeId}?level=${areaDataLevel}&parentId=${areaDataParentId}`,
              { skipGlobalLoading: true },
            );
            if (response.data?.success) {
              setHierarchyData((prev) => ({
                ...prev,
                [`level${areaDataLevel}`]: response.data.data,
              }));
            }
          } catch (error) {
            console.error(
              `Error fetching area data level ${areaDataLevel}:`,
              error,
            );
          }
        }
      }
    };

    fetchChildren();
  }, [
    bridgeId,
    getAreaDataLevel,
    getEnabledLevels,
    selectedBridge,
    selectedPath,
  ]);

  const collectAreaElementsFromParentIds = useCallback(
    async (startLevel, parentIds) => {
      if (!bridgeId) return [];
      const areaDataLevel = getAreaDataLevel();
      const uniqueParentIds = Array.from(
        new Set((parentIds || []).filter(Boolean)),
      );

      if (uniqueParentIds.length === 0) return [];

      if (startLevel === areaDataLevel) {
        const areaLevelKey = `level${areaDataLevel}`;
        const loaded = hierarchyData[areaLevelKey] || [];
        const byId = new Map(loaded.map((element) => [element.id, element]));
        return uniqueParentIds.map((id) => byId.get(id) || { id });
      }

      let currentParentIds = uniqueParentIds;
      let currentElements = [];

      for (let level = startLevel + 1; level <= areaDataLevel; level += 1) {
        const responses = await Promise.all(
          currentParentIds.map((parentId) =>
            api.get(
              `/new-simulation/hierarchy/${bridgeId}?level=${level}&parentId=${parentId}`,
              { skipGlobalLoading: true },
            ),
          ),
        );

        const merged = [];
        const seen = new Set();

        responses.forEach((response) => {
          if (!response.data?.success || !Array.isArray(response.data.data)) {
            return;
          }
          response.data.data.forEach((element) => {
            if (!element?.id || seen.has(element.id)) return;
            seen.add(element.id);
            merged.push(element);
          });
        });

        currentElements = merged;
        currentParentIds = merged.map((element) => element.id);
        if (currentParentIds.length === 0) break;
      }

      return currentElements;
    },
    [bridgeId, getAreaDataLevel, hierarchyData],
  );

  const selectedAreaSummary = useMemo(() => {
    if (selectedElementIds.length === 0) return { totalArea: 0, count: 0 };
    if (selectionTableRows.length > 0) {
      return {
        totalArea: selectionTableRows.reduce(
          (sum, row) => sum + (Number(row.area) || 0),
          0,
        ),
        count: selectedElementIds.length,
      };
    }

    const areaDataLevel = getAreaDataLevel();
    const areaLevelKey = `level${areaDataLevel}`;
    let totalArea = 0;

    selectedElementIds.forEach((id) => {
      const element =
        selectedElementsCache[id] ||
        hierarchyData[areaLevelKey]?.find((item) => item.id === id);
      const area =
        element?.total_area != null ? parseFloat(element.total_area) : 0;
      if (!Number.isNaN(area)) {
        totalArea += area;
      }
    });

    return { totalArea, count: selectedElementIds.length };
  }, [
    getAreaDataLevel,
    hierarchyData,
    selectionTableRows,
    selectedElementIds,
    selectedElementsCache,
  ]);

  useEffect(() => {
    const buildSelectionTableRows = async () => {
      if (!bridgeId || selectedElementIds.length === 0) {
        setSelectionTableRows([]);
        return;
      }

      const selectionSignature = selectedElementIds
        .map((id) => String(id))
        .join(",");
      const shouldHydrateVisiblePath =
        pendingVisiblePathHydrationRef.current === selectionSignature;

      if (!showSelectionSummary && !shouldHydrateVisiblePath) {
        setSelectionTableRows([]);
        return;
      }

      try {
        if (showSelectionSummary) {
          setLoadingSelectionTable(true);
        }
        const response = await api.post(
          "/new-simulation/hierarchy-structure",
          {
            bridgeId: Number(bridgeId),
            elementIds: selectedElementIds,
          },
          { skipGlobalLoading: true },
        );

        if (!response.data?.success || !response.data?.data?.hierarchy) {
          setSelectionTableRows([]);
          return;
        }

        const rows = [];
        const getName = (node) =>
          node?.name ||
          node?.element_name ||
          node?.code ||
          node?.element_code ||
          "";

        const visit = (node, context = {}) => {
          const levelNum = node.levelNumber || node.level_number;
          const current = { ...context };

          if (levelNum != null) {
            current[`level${levelNum}`] = getName(node);
          }

          if (node.direction && !current.direction)
            current.direction = node.direction;
          if (node.position && !current.position)
            current.position = node.position;

          if (node.isSelected) {
            rows.push({
              id: node.id,
              direction: current.direction || "-",
              position: current.position || "-",
              levels: { ...current },
              area: parseFloat(node.area || node.total_area || 0) || 0,
            });
          }

          (node.children || []).forEach((child) => visit(child, current));
        };

        response.data.data.hierarchy.forEach((root) => visit(root, {}));
        rows.sort((a, b) =>
          String(a.id).localeCompare(String(b.id), undefined, {
            numeric: true,
          }),
        );
        setSelectionTableRows(rows);
        setSelectedElementsCache((previousValue) => {
          const nextValue = { ...previousValue };
          rows.forEach((row) => {
            if (!nextValue[row.id]) {
              nextValue[row.id] = {
                id: row.id,
                area: row.area,
                total_area: row.area,
              };
            }
          });
          return nextValue;
        });

        const hydratedAreaDataLevel = Number(
          response.data.data.areaDataLevel || getAreaDataLevel(),
        );
        const hydratedPenultimateLevel = hydratedAreaDataLevel - 1;
        const selectedPenultimateParentIds = new Set();

        if (hydratedPenultimateLevel >= 1) {
          const collectSelectedPenultimateParents = (
            nodes,
            activePenultimateParentId = null,
          ) => {
            (nodes || []).forEach((node) => {
              const levelNum = Number(node.levelNumber || node.level_number);
              const nextPenultimateParentId =
                levelNum === hydratedPenultimateLevel
                  ? node.id
                  : activePenultimateParentId;

              if (
                levelNum === hydratedAreaDataLevel &&
                node.isSelected &&
                nextPenultimateParentId
              ) {
                selectedPenultimateParentIds.add(nextPenultimateParentId);
              }

              collectSelectedPenultimateParents(
                node.children || [],
                nextPenultimateParentId,
              );
            });
          };

          collectSelectedPenultimateParents(response.data.data.hierarchy, null);

          if (selectedPenultimateParentIds.size > 0) {
            const descendantEntries = await Promise.all(
              Array.from(selectedPenultimateParentIds).map(async (parentId) => {
                const descendants = await collectAreaElementsFromParentIds(
                  hydratedPenultimateLevel,
                  [parentId],
                );

                return [
                  parentId,
                  descendants.map((element) => element.id).filter(Boolean),
                ];
              }),
            );

            setDescendantAreaIdsByParentId((previousValue) => ({
              ...previousValue,
              ...Object.fromEntries(descendantEntries),
            }));
          }
        }

        if (pendingVisiblePathHydrationRef.current === selectionSignature) {
          const findFirstSelectedBranch = (nodes, path = []) => {
            for (const node of nodes || []) {
              const nextPath = [...path, node];
              if (node.isSelected) {
                return nextPath;
              }

              const childPath = findFirstSelectedBranch(
                node.children || [],
                nextPath,
              );
              if (childPath.length > 0) {
                return childPath;
              }
            }

            return [];
          };

          const branchNodes = findFirstSelectedBranch(
            response.data.data.hierarchy,
          );

          if (branchNodes.length > 0) {
            const areaDataLevel = Number(response.data.data.areaDataLevel || 0);
            const branchNodeByLevel = new Map(
              branchNodes.map((node) => [Number(node.levelNumber), node]),
            );
            const derivedDirection =
              branchNodes.find((node) => node.direction)?.direction || "";
            const derivedPosition =
              branchNodes.find((node) => node.position)?.position || "";

            const nextPath = {};
            if (derivedDirection) {
              nextPath.direction = derivedDirection;
            }
            if (derivedPosition) {
              nextPath.position = derivedPosition;
            }

            try {
              setLoadingHierarchy(true);

              const nextHierarchyData = {};

              if (derivedDirection) {
                const positionsResponse = await api.get(
                  `/new-simulation/positions/${bridgeId}?direction=${encodeURIComponent(derivedDirection)}`,
                  { skipGlobalLoading: true },
                );
                nextHierarchyData.positions = positionsResponse.data?.success
                  ? positionsResponse.data.data
                  : [];
              }

              let level1Url = `/new-simulation/hierarchy/${bridgeId}?level=1`;
              if (derivedDirection) {
                level1Url += `&direction=${encodeURIComponent(derivedDirection)}`;
              }
              if (derivedPosition) {
                level1Url += `&position=${encodeURIComponent(derivedPosition)}`;
              }

              const level1Response = await api.get(level1Url, {
                skipGlobalLoading: true,
              });
              nextHierarchyData.level1 =
                level1Response.data?.success &&
                Array.isArray(level1Response.data.data)
                  ? level1Response.data.data
                  : [];

              const level1Node = branchNodeByLevel.get(1);
              if (level1Node) {
                nextPath.level1 = level1Node.id;
              }

              let parentId = level1Node?.id || null;
              for (
                let level = 2;
                level <= areaDataLevel && parentId;
                level += 1
              ) {
                const childResponse = await api.get(
                  `/new-simulation/hierarchy/${bridgeId}?level=${level}&parentId=${parentId}`,
                  { skipGlobalLoading: true },
                );

                nextHierarchyData[`level${level}`] =
                  childResponse.data?.success &&
                  Array.isArray(childResponse.data.data)
                    ? childResponse.data.data
                    : [];

                const branchNode = branchNodeByLevel.get(level);
                const stopAtSharedParent =
                  clearSelectionOnBranchChange &&
                  selectedPenultimateParentIds.size > 1 &&
                  level >= hydratedPenultimateLevel;

                if (branchNode && level < areaDataLevel && !stopAtSharedParent) {
                  nextPath[`level${level}`] = branchNode.id;
                }

                if (stopAtSharedParent) {
                  break;
                }

                parentId = branchNode?.id || null;
              }

              setHierarchyData((previousValue) => ({
                ...previousValue,
                ...nextHierarchyData,
              }));
              setSelectedPath((previousValue) => {
                if (
                  previousValue.direction ||
                  previousValue.position ||
                  Object.keys(previousValue).some((key) =>
                    key.startsWith("level"),
                  )
                ) {
                  return previousValue;
                }

                return nextPath;
              });
            } catch (error) {
              console.error(
                "Error hydrating repair project hierarchy path:",
                error,
              );
            } finally {
              setLoadingHierarchy(false);
            }
          }

          pendingVisiblePathHydrationRef.current = null;
        }
      } catch (error) {
        console.error("Error building selection table rows:", error);
        setSelectionTableRows([]);
      } finally {
        setLoadingSelectionTable(false);
      }
    };

    buildSelectionTableRows();
  }, [
    bridgeId,
    clearSelectionOnBranchChange,
    collectAreaElementsFromParentIds,
    getAreaDataLevel,
    selectedElementIds,
    showSelectionSummary,
  ]);

  const selectedElementsForCallback = useMemo(() => {
    const areaDataLevel = getAreaDataLevel();
    const areaLevelKey = `level${areaDataLevel}`;

    return selectedElementIds
      .map(
        (id) =>
          selectedElementsCache[id] ||
          hierarchyData[areaLevelKey]?.find((item) => item.id === id),
      )
      .filter(Boolean);
  }, [
    getAreaDataLevel,
    hierarchyData,
    selectedElementIds,
    selectedElementsCache,
  ]);

  const notifyUserSelectionChange = useCallback(
    (nextIds, nextCache = selectedElementsCache) => {
      if (!bridgeId || typeof onSelectionChange !== "function") return;

      const normalizedIds = normalizeSelectionIds(nextIds);
      const selectionSignature = normalizedIds.map((id) => String(id)).join(",");
      const areaDataLevel = getAreaDataLevel();
      const areaLevelKey = `level${areaDataLevel}`;
      const callbackElements = normalizedIds
        .map(
          (id) =>
            nextCache?.[id] ||
            hierarchyData[areaLevelKey]?.find((item) => item.id === id),
        )
        .filter(Boolean);

      hydratingSelectionSignatureRef.current = null;
      lastNotifiedSelectionRef.current = selectionSignature;
      onSelectionChange(normalizedIds, callbackElements);
    },
    [
      bridgeId,
      getAreaDataLevel,
      hierarchyData,
      onSelectionChange,
      selectedElementsCache,
    ],
  );

  const commitUserSelection = useCallback(
    (nextIds, nextCache) => {
      const normalizedIds = normalizeSelectionIds(nextIds);
      setSelectedElementIds(normalizedIds);
      setSelectedElementsCache(nextCache || {});
      notifyUserSelectionChange(normalizedIds, nextCache || {});
    },
    [notifyUserSelectionChange],
  );

  useEffect(() => {
    if (!bridgeId || typeof onSelectionChange !== "function") return;
    const selectionSignature = selectedElementIds
      .map((id) => String(id))
      .join(",");

    if (hydratingSelectionSignatureRef.current !== null) {
      if (selectionSignature === hydratingSelectionSignatureRef.current) {
        lastNotifiedSelectionRef.current = selectionSignature;
        hydratingSelectionSignatureRef.current = null;
      }
      return;
    }

    if (lastNotifiedSelectionRef.current === selectionSignature) {
      return;
    }

    lastNotifiedSelectionRef.current = selectionSignature;
    onSelectionChange(selectedElementIds, selectedElementsForCallback);
  }, [
    bridgeId,
    onSelectionChange,
    selectedElementIds,
    selectedElementsForCallback,
  ]);

  useEffect(() => {
    if (!penultimateLevel) return;
    const selectedParentId = selectedPath[`level${penultimateLevel}`];
    const areaLevelKey = `level${getAreaDataLevel()}`;
    const visibleAreaIds = (hierarchyData[areaLevelKey] || [])
      .map((element) => element.id)
      .filter(Boolean);

    if (selectedParentId && visibleAreaIds.length > 0) {
      setDescendantAreaIdsByParentId((prev) => ({
        ...prev,
        [selectedParentId]: visibleAreaIds,
      }));
    }
  }, [getAreaDataLevel, hierarchyData, penultimateLevel, selectedPath]);

  const handleDirectionListboxSelect = async (direction) => {
    if (clearSelectionOnBranchChange && selectedPath.direction !== direction) {
      clearCurrentSelection();
    }

    const newPath = {};
    if (direction) {
      newPath.direction = direction;
    }
    setSelectedPath(newPath);
    setHierarchyData((prev) => ({
      directions: prev.directions || [],
      positions: prev.positions || [],
    }));

    if (!bridgeId) return;

    try {
      setLoadingHierarchy(true);
      if (direction) {
        const [positionsResponse, level1Response] = await Promise.all([
          api.get(
            `/new-simulation/positions/${bridgeId}?direction=${encodeURIComponent(direction)}`,
          ),
          api.get(
            `/new-simulation/hierarchy/${bridgeId}?level=1&direction=${encodeURIComponent(direction)}`,
          ),
        ]);

        let positions = positionsResponse.data?.success
          ? positionsResponse.data.data
          : [];

        if (!positions.length) {
          const fallbackPositionsResponse = await api.get(
            `/new-simulation/positions/${bridgeId}`,
          );
          positions = fallbackPositionsResponse.data?.success
            ? fallbackPositionsResponse.data.data
            : [];
        }

        setHierarchyData((prev) => ({
          ...prev,
          positions,
          level1: level1Response.data?.success ? level1Response.data.data : [],
        }));
      } else {
        const [positionsResponse, level1Response] = await Promise.all([
          api.get(`/new-simulation/positions/${bridgeId}`),
          api.get(`/new-simulation/hierarchy/${bridgeId}?level=1`),
        ]);

        setHierarchyData((prev) => ({
          ...prev,
          positions: positionsResponse.data?.success
            ? positionsResponse.data.data
            : [],
          level1: level1Response.data?.success ? level1Response.data.data : [],
        }));
      }
    } catch (error) {
      console.error("Error fetching after direction select:", error);
    } finally {
      setLoadingHierarchy(false);
    }
  };

  const handlePositionListboxSelect = async (position) => {
    if (
      clearSelectionOnBranchChange &&
      (selectedPath.position !== position ||
        Object.keys(selectedPath).some((key) => key.startsWith("level")))
    ) {
      clearCurrentSelection();
    }

    const newPath = { ...selectedPath };
    if (position) {
      newPath.position = position;
    } else {
      delete newPath.position;
    }
    for (let i = 1; i <= 8; i += 1) {
      delete newPath[`level${i}`];
    }
    setSelectedPath(newPath);
    setHierarchyData((prev) => ({
      directions: prev.directions || [],
      positions: prev.positions || [],
    }));

    if (!bridgeId) return;

    try {
      setLoadingHierarchy(true);
      let level1Url = `/new-simulation/hierarchy/${bridgeId}?level=1`;
      if (newPath.direction) {
        level1Url += `&direction=${encodeURIComponent(newPath.direction)}`;
      }
      if (position) {
        level1Url += `&position=${encodeURIComponent(position)}`;
      }

      const response = await api.get(level1Url);
      if (!response.data?.success) {
        return;
      }

      const level1Data = Array.isArray(response.data.data)
        ? response.data.data
        : [];

      setHierarchyData((prev) => ({
        ...prev,
        level1: level1Data,
      }));

      if (level1Data.length === 1 && newPath.direction) {
        const level1Id = level1Data[0].id;
        setSelectedPath((prev) => ({ ...prev, level1: level1Id }));

        const level2Response = await api.get(
          `/new-simulation/hierarchy/${bridgeId}?level=2&parentId=${level1Id}`,
        );

        if (
          level2Response.data?.success &&
          Array.isArray(level2Response.data.data)
        ) {
          const level2Data = level2Response.data.data;
          setHierarchyData((prev) => ({
            ...prev,
            level2: level2Data,
          }));

          const normalizeValue = (value) =>
            (value || "").toString().trim().toLowerCase();
          let matchedLevel2 = null;

          if (position) {
            const targetPosition = normalizeValue(position);
            matchedLevel2 =
              level2Data.find((element) => {
                const candidates = [
                  element.position,
                  element.element_name,
                  element.element_code,
                ]
                  .map(normalizeValue)
                  .filter(Boolean);

                return candidates.includes(targetPosition);
              }) || null;
          }

          if (!matchedLevel2 && level2Data.length === 1) {
            matchedLevel2 = level2Data[0];
          }

          if (matchedLevel2) {
            const level2Id = matchedLevel2.id;
            setSelectedPath((prev) => ({
              ...prev,
              level1: level1Id,
              level2: level2Id,
            }));

            const level3Response = await api.get(
              `/new-simulation/hierarchy/${bridgeId}?level=3&parentId=${level2Id}`,
            );

            if (
              level3Response.data?.success &&
              Array.isArray(level3Response.data.data)
            ) {
              setHierarchyData((prev) => ({
                ...prev,
                level3: level3Response.data.data,
              }));
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching after position select:", error);
    } finally {
      setLoadingHierarchy(false);
    }
  };

  const handleLevelSelection = (level, elementId) => {
    const areaDataLevel = getAreaDataLevel();
    if (level === areaDataLevel) return;

    const hasLowerPathSelection = Object.keys(selectedPath).some((key) => {
      const match = key.match(/^level(\d+)$/);
      return match && Number(match[1]) > level;
    });
    const selectedDescendantIds = descendantAreaIdsByParentId[elementId] || [];
    const isExistingSelectedParent =
      level === penultimateLevel &&
      selectedDescendantIds.some((id) => selectedElementIds.includes(id));

    if (
      clearSelectionOnBranchChange &&
      !isExistingSelectedParent &&
      (selectedPath[`level${level}`] !== elementId || hasLowerPathSelection)
    ) {
      clearCurrentSelection();
    }

    const newPath = { ...selectedPath };
    if (elementId) {
      newPath[`level${level}`] = elementId;
      for (let i = level + 1; i <= 8; i += 1) {
        delete newPath[`level${i}`];
      }
    } else {
      delete newPath[`level${level}`];
      for (let i = level + 1; i <= 8; i += 1) {
        delete newPath[`level${i}`];
      }
    }

    setSelectedPath(newPath);
    setHierarchyData((prev) => {
      const next = { ...prev };
      for (let i = level + 1; i <= 8; i += 1) {
        delete next[`level${i}`];
      }
      return next;
    });
  };

  const handleAreaDataSelection = (elementId, isChecked) => {
    const areaDataLevel = getAreaDataLevel();
    const areaLevelKey = `level${areaDataLevel}`;
    const nextCache = { ...selectedElementsCache };
    let nextIds = selectedElementIds;

    if (isChecked) {
      nextIds = selectedElementIds.includes(elementId)
        ? selectedElementIds
        : [...selectedElementIds, elementId];
      const element = hierarchyData[areaLevelKey]?.find(
        (item) => item.id === elementId,
      );
      if (element) {
        nextCache[elementId] = element;
      }
      setSelectedPath((prev) => ({
        ...prev,
        [`level${areaDataLevel}`]: elementId,
      }));
    } else {
      nextIds = selectedElementIds.filter((id) => id !== elementId);
      delete nextCache[elementId];
      if (selectedPath[`level${areaDataLevel}`] === elementId) {
        setSelectedPath((prev) => {
          const next = { ...prev };
          delete next[`level${areaDataLevel}`];
          return next;
        });
      }
    }

    commitUserSelection(nextIds, nextCache);
  };

  const handleSelectAllAcrossBridge = useCallback(
    async (isChecked) => {
      if (!bridgeId || !selectedBridge) return;

      if (!isChecked) {
        commitUserSelection([], {});
        setDescendantAreaIdsByParentId({});
        return;
      }

      try {
        setBulkSelecting(true);
        const areaDataLevel = getAreaDataLevel();
        const response = await api.get(
          `/new-simulation/hierarchy/${bridgeId}?level=${areaDataLevel}`,
          { skipGlobalLoading: true },
        );
        if (!response.data?.success) return;

        const allElements = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        const allIds = allElements.map((element) => element.id);
        const cacheUpdate = {};
        allElements.forEach((element) => {
          cacheUpdate[element.id] = element;
        });

        commitUserSelection(allIds, cacheUpdate);
      } catch (error) {
        console.error("Error selecting all repair project elements:", error);
      } finally {
        setBulkSelecting(false);
      }
    },
    [bridgeId, commitUserSelection, getAreaDataLevel, selectedBridge],
  );

  const applyBulkAreaSelection = useCallback((areaElements, isChecked, replaceSelection = false) => {
    const uniqueElements = [];
    const seen = new Set();

    (areaElements || []).forEach((element) => {
      if (!element?.id || seen.has(element.id)) return;
      seen.add(element.id);
      uniqueElements.push(element);
    });

    if (uniqueElements.length === 0) return;

    const ids = uniqueElements.map((element) => element.id);
    const idSet = new Set(ids);

    if (isChecked) {
      const nextIds = replaceSelection
        ? ids
        : Array.from(new Set([...(selectedElementIds || []), ...ids]));
      const nextCache = replaceSelection ? {} : { ...(selectedElementsCache || {}) };
      uniqueElements.forEach((element) => {
        nextCache[element.id] = element;
      });
      commitUserSelection(nextIds, nextCache);
    } else {
      const nextIds = (selectedElementIds || []).filter((id) => !idSet.has(id));
      const nextCache = { ...(selectedElementsCache || {}) };
      ids.forEach((id) => delete nextCache[id]);
      commitUserSelection(nextIds, nextCache);
    }
  }, [commitUserSelection, selectedElementIds, selectedElementsCache]);

  const handleLevelCascadeSelectAll = useCallback(
    async (levelNum, isChecked) => {
      const levelKey = `level${levelNum}`;
      const elements = hierarchyData[levelKey] || [];
      const ids = elements.map((element) => element.id).filter(Boolean);
      if (!ids.length) return;

      try {
        setBulkSelecting(true);
        const areaElements = await collectAreaElementsFromParentIds(
          levelNum,
          ids,
        );
        applyBulkAreaSelection(
          areaElements,
          isChecked,
          clearSelectionOnBranchChange &&
            isChecked &&
            (penultimateLevel == null || levelNum < penultimateLevel),
        );
      } catch (error) {
        console.error(
          `Error cascading select all for level ${levelNum}:`,
          error,
        );
      } finally {
        setBulkSelecting(false);
      }
    },
    [
      applyBulkAreaSelection,
      clearSelectionOnBranchChange,
      collectAreaElementsFromParentIds,
      hierarchyData,
      penultimateLevel,
    ],
  );

  const handlePositionCascadeSelectAll = useCallback(
    async (isChecked) => {
      if (!bridgeId || !selectedPath.direction) return;

      try {
        setBulkSelecting(true);
        const level1Response = await api.get(
          `/new-simulation/hierarchy/${bridgeId}?level=1&direction=${encodeURIComponent(selectedPath.direction)}`,
          { skipGlobalLoading: true },
        );
        const level1Elements =
          level1Response.data?.success &&
          Array.isArray(level1Response.data.data)
            ? level1Response.data.data
            : [];
        if (!level1Elements.length) return;

        const areaElements = await collectAreaElementsFromParentIds(
          1,
          level1Elements.map((element) => element.id),
        );
        applyBulkAreaSelection(
          areaElements,
          isChecked,
          clearSelectionOnBranchChange && isChecked,
        );
      } catch (error) {
        console.error("Error cascading select all for positions:", error);
      } finally {
        setBulkSelecting(false);
      }
    },
    [
      applyBulkAreaSelection,
      bridgeId,
      clearSelectionOnBranchChange,
      collectAreaElementsFromParentIds,
      selectedPath.direction,
    ],
  );

  const handlePenultimateSelection = async (levelNum, element, isChecked) => {
    try {
      setBulkSelecting(true);
      const areaElements = await collectAreaElementsFromParentIds(levelNum, [
        element.id,
      ]);
      setDescendantAreaIdsByParentId((prev) => ({
        ...prev,
        [element.id]: areaElements.map((item) => item.id).filter(Boolean),
      }));
      applyBulkAreaSelection(areaElements, isChecked);
    } catch (error) {
      console.error("Error selecting descendants for repair project:", error);
    } finally {
      setBulkSelecting(false);
    }
  };

  const totalAreaEntityLabel = useMemo(() => {
    const singular = String(
      getRawLevelLabel(getAreaDataLevel()) || "Element",
    ).trim();
    if (!singular) return "Elements";
    return singular.toLowerCase().endsWith("s") ? singular : `${singular}s`;
  }, [getAreaDataLevel, getRawLevelLabel]);

  const selectionSummaryText = `${selectedElementIds.length} ${totalAreaEntityLabel.toLowerCase()} selected`;
  const selectionAreaText = `${selectedAreaSummary.totalArea.toFixed(2)} m²`;

  if (!bridgeId) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center">
          <button
            type="button"
            className="mr-3 text-gray-500 hover:text-gray-700"
            onClick={() => setIsCollapsed((prev) => !prev)}
            title={isCollapsed ? "Expand selection" : "Collapse selection"}
          >
            <FontAwesomeIcon
              icon={isCollapsed ? faChevronRight : faChevronDown}
            />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Select Elements <span className="text-red-500">*</span>
            </h2>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-base font-bold text-gray-900 sm:text-lg">
                {selectionSummaryText}
              </span>
              <span className="text-base font-bold text-blue-700 sm:text-lg">
                • {selectionAreaText}
              </span>
            </div>
          </div>
        </div>
      </div>

      {!isCollapsed && (
        <>
          <p className="text-sm text-gray-600 mb-4">
            Select elements through the hierarchy. The last two lists support
            multi-select checkboxes.
          </p>

          {showTopFilters && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Item
                </label>
                <select
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedPath.direction || ""}
                  onChange={(event) =>
                    handleDirectionListboxSelect(event.target.value || null)
                  }
                >
                  <option value="">All Items</option>
                  {(hierarchyData.directions || []).map((direction) => (
                    <option key={direction} value={direction}>
                      {direction}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Position
                </label>
                <select
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedPath.position || ""}
                  onChange={(event) =>
                    handlePositionListboxSelect(event.target.value || null)
                  }
                  disabled={!selectedPath.direction}
                >
                  <option value="">All Positions</option>
                  {(hierarchyData.positions || []).map((position) => {
                    const displayPosition =
                      position === "Internal" || position === "External"
                        ? position
                        : String(position)
                            .replace(/^P\d+\s*/i, "")
                            .trim() || position;
                    return (
                      <option key={position} value={position}>
                        {displayPosition}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          )}

          <div className="flex gap-2 overflow-x-auto pb-4">
            <div
              className="flex-shrink-0"
              style={{ minWidth: "140px", maxWidth: "140px" }}
            >
              <label className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                <span>Item</span>
                <input
                  type="checkbox"
                  title="Select all bridge elements"
                  onChange={(event) =>
                    handleSelectAllAcrossBridge(event.target.checked)
                  }
                  disabled={!bridgeId || bulkSelecting}
                />
              </label>
              <div
                className="border border-gray-300 rounded-md bg-white"
                style={{
                  height: "416px",
                  minHeight: "416px",
                  overflowY: "auto",
                }}
              >
                {loadingHierarchy &&
                !(hierarchyData.directions || []).length ? (
                  <div className="p-2 text-center text-gray-500 text-xs">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                    Loading...
                  </div>
                ) : !(hierarchyData.directions || []).length ? (
                  <div className="p-2 text-center text-gray-400 text-xs">
                    No items
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {(hierarchyData.directions || []).map((direction) => {
                      const isSelected = selectedPath.direction === direction;
                      return (
                        <div
                          key={direction}
                          className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                            isSelected
                              ? "bg-blue-100 border-l-2 border-blue-500"
                              : ""
                          }`}
                          onClick={() =>
                            handleDirectionListboxSelect(
                              isSelected ? null : direction,
                            )
                          }
                        >
                          <div
                            className="font-medium text-xs text-gray-900 truncate"
                            title={direction}
                          >
                            {direction}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {(hierarchyData.directions || []).length > 0 && (
                <div className="mt-0.5 text-xs text-gray-500 text-center">
                  {hierarchyData.directions.length}
                </div>
              )}
            </div>

            <div
              className="flex-shrink-0"
              style={{ minWidth: "140px", maxWidth: "140px" }}
            >
              <label className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                <span>Position</span>
                <input
                  type="checkbox"
                  title="Select all positions and descendants"
                  onChange={(event) =>
                    handlePositionCascadeSelectAll(event.target.checked)
                  }
                  disabled={!selectedPath.direction || bulkSelecting}
                />
              </label>
              <div
                className="border border-gray-300 rounded-md bg-white"
                style={{
                  height: "416px",
                  minHeight: "416px",
                  overflowY: "auto",
                }}
              >
                {!selectedPath.direction ? (
                  <div className="p-2 text-center text-gray-400 text-xs">
                    Select Item first
                  </div>
                ) : loadingHierarchy &&
                  !(hierarchyData.positions || []).length ? (
                  <div className="p-2 text-center text-gray-500 text-xs">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                    Loading...
                  </div>
                ) : !(hierarchyData.positions || []).length ? (
                  <div className="p-2 text-center text-gray-400 text-xs">
                    No positions
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {(hierarchyData.positions || []).map((position) => {
                      const isSelected = selectedPath.position === position;
                      const displayPosition =
                        position === "Internal" || position === "External"
                          ? position
                          : String(position)
                              .replace(/^P\d+\s*/i, "")
                              .trim() || position;
                      return (
                        <div
                          key={position}
                          className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                            isSelected
                              ? "bg-blue-100 border-l-2 border-blue-500"
                              : ""
                          }`}
                          onClick={() =>
                            handlePositionListboxSelect(
                              isSelected ? null : position,
                            )
                          }
                        >
                          <div
                            className="font-medium text-xs text-gray-900 truncate"
                            title={displayPosition}
                          >
                            {displayPosition}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {(hierarchyData.positions || []).length > 0 && (
                <div className="mt-0.5 text-xs text-gray-500 text-center">
                  {hierarchyData.positions.length}
                </div>
              )}
            </div>

            {levelsToShowAfterFilters.map((levelNum) => {
              const levelKey = `level${levelNum}`;
              const levelLabel = getLevelLabel(levelNum);
              const areaDataLevel = getAreaDataLevel();
              const isAreaDataLevel = levelNum === areaDataLevel;
              const isPenultimateLevel = levelNum === penultimateLevel;
              const elements = hierarchyData[levelKey] || [];
              const selectedId = selectedPath[levelKey] || null;
              const isLevelEnabled =
                levelNum === firstVisibleLevel
                  ? Boolean(selectedPath.direction && selectedPath.position)
                  : Boolean(selectedPath[`level${levelNum - 1}`]);
              const areaLevelKey = `level${areaDataLevel}`;

              return (
                <div
                  key={levelNum}
                  className="flex-shrink-0"
                  style={{ minWidth: "140px", maxWidth: "140px" }}
                >
                  <label className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                    <span>
                      {levelLabel}
                      {isAreaDataLevel && (
                        <span className="ml-1 text-xs text-blue-600 font-normal">
                          (Area)
                        </span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      title={`Select all ${levelLabel} and descendants`}
                      onChange={(event) =>
                        handleLevelCascadeSelectAll(
                          levelNum,
                          event.target.checked,
                        )
                      }
                      disabled={
                        !elements.length || !isLevelEnabled || bulkSelecting
                      }
                    />
                  </label>
                  <div
                    className="border border-gray-300 rounded-md bg-white"
                    style={{
                      height: "416px",
                      minHeight: "416px",
                      overflowY: "auto",
                    }}
                  >
                    {loadingHierarchy && elements.length === 0 ? (
                      <div className="p-2 text-center text-gray-500 text-xs">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-1"
                        />
                        Loading...
                      </div>
                    ) : elements.length === 0 ? (
                      <div className="p-2 text-center text-gray-400 text-xs">
                        {isLevelEnabled
                          ? "No items"
                          : levelNum === firstVisibleLevel
                            ? "Select Item and Position"
                            : "Select parent"}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {elements.map((element) => {
                          const displayName =
                            element.element_name ||
                            element.element_code ||
                            `ID ${element.id}`;

                          if (isAreaDataLevel) {
                            const isSelected = selectedElementIds.includes(
                              element.id,
                            );
                            return (
                              <div
                                key={element.id}
                                className={`py-0.5 px-1 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                  isSelected
                                    ? "bg-blue-100 border-l-2 border-blue-500"
                                    : ""
                                }`}
                                onClick={() => {
                                  if (isLevelEnabled) {
                                    handleAreaDataSelection(
                                      element.id,
                                      !isSelected,
                                    );
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(event) => {
                                    event.stopPropagation();
                                    handleAreaDataSelection(
                                      element.id,
                                      event.target.checked,
                                    );
                                  }}
                                  onClick={(event) => event.stopPropagation()}
                                  className="cursor-pointer flex-shrink-0"
                                  disabled={!isLevelEnabled}
                                />
                                <div
                                  className="flex-1 min-w-0 font-medium text-xs text-gray-900 truncate"
                                  title={displayName}
                                >
                                  {displayName}
                                </div>
                              </div>
                            );
                          }

                          if (isPenultimateLevel) {
                            const cachedDescendantIds =
                              descendantAreaIdsByParentId[element.id] ||
                              (selectedId === element.id
                                ? (hierarchyData[areaLevelKey] || [])
                                    .map((item) => item.id)
                                    .filter(Boolean)
                                : []);
                            const isChecked =
                              cachedDescendantIds.length > 0 &&
                              cachedDescendantIds.every((id) =>
                                selectedElementIds.includes(id),
                              );

                            return (
                              <div
                                key={element.id}
                                className={`py-0.5 px-1 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                                  selectedId === element.id
                                    ? "bg-blue-100 border-l-2 border-blue-500"
                                    : ""
                                }`}
                                onClick={() => {
                                  if (isLevelEnabled) {
                                    handleLevelSelection(
                                      levelNum,
                                      selectedId === element.id
                                        ? null
                                        : element.id,
                                    );
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(event) => {
                                    event.stopPropagation();
                                    handlePenultimateSelection(
                                      levelNum,
                                      element,
                                      event.target.checked,
                                    );
                                  }}
                                  onClick={(event) => event.stopPropagation()}
                                  className="cursor-pointer flex-shrink-0"
                                  disabled={!isLevelEnabled || bulkSelecting}
                                />
                                <div
                                  className="flex-1 min-w-0 font-medium text-xs text-gray-900 truncate"
                                  title={displayName}
                                >
                                  {displayName}
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div
                              key={element.id}
                              className={`p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                                selectedId === element.id
                                  ? "bg-blue-100 border-l-2 border-blue-500"
                                  : ""
                              }`}
                              onClick={() => {
                                if (isLevelEnabled) {
                                  handleLevelSelection(
                                    levelNum,
                                    selectedId === element.id
                                      ? null
                                      : element.id,
                                  );
                                }
                              }}
                            >
                              <div
                                className="font-medium text-xs text-gray-900 truncate"
                                title={displayName}
                              >
                                {displayName}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {elements.length > 0 && (
                    <div className="mt-0.5 text-xs text-gray-500 text-center">
                      {elements.length}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {showSelectionSummary && (
          <div className="mt-4 overflow-hidden rounded-md border border-gray-300 bg-gray-50">
            <button
              type="button"
              onClick={() => setIsSummaryCollapsed((prev) => !prev)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-700">
                  Selection Summary
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Expand to review the selected{" "}
                  {totalAreaEntityLabel.toLowerCase()}.
                </p>
              </div>
              <span className="flex items-center gap-3">
                <span className="hidden text-sm font-semibold text-gray-700 sm:inline">
                  {selectionSummaryText}
                </span>
                <FontAwesomeIcon
                  icon={isSummaryCollapsed ? faChevronRight : faChevronDown}
                  className="text-gray-500"
                />
              </span>
            </button>

            {!isSummaryCollapsed &&
              (loadingSelectionTable ? (
                <div className="border-t border-gray-300 px-4 py-3 text-sm text-gray-500">
                  Building table...
                </div>
              ) : (
                <div className="border-t border-gray-300 p-4">
                  <div
                    className="overflow-x-auto overflow-y-auto"
                    style={{ maxHeight: "390px" }}
                  >
                    <table className="min-w-full rounded border border-gray-200 bg-white text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          {selectionSummaryLevels.map((levelNum) => (
                            <th
                              key={levelNum}
                              className="sticky top-0 z-10 bg-gray-100 px-3 py-2 text-left font-semibold text-gray-700"
                            >
                              {getRawLevelLabel(levelNum)}
                            </th>
                          ))}
                          <th className="sticky top-0 z-10 bg-gray-100 px-3 py-2 text-right font-semibold text-gray-700">
                            Area (m²)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectionTableRows.map((row) => (
                          <tr key={row.id} className="border-t border-gray-200">
                            {selectionSummaryLevels.map((levelNum) => (
                              <td
                                key={`${row.id}-${levelNum}`}
                                className="px-3 py-2 text-gray-900"
                              >
                                {row.levels[`level${levelNum}`] ||
                                  (levelNum === 1
                                    ? row.direction || "-"
                                    : levelNum === 2
                                      ? row.position || "-"
                                      : "-")}
                              </td>
                            ))}
                            <td className="px-3 py-2 text-right text-gray-900">
                              {(row.area || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
          </div>
          )}
        </>
      )}
    </div>
  );
};

export default RepairProjectHierarchySelector;
